import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import {
  Grid,
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomButton from "../components/Custom/CustomButton";
import { RZP_KEY } from "../globals/const";
import {
  capturePayment,
  getBiocharOrderDetails,
  createOrder,
  processCashPayment,
} from "../api/biocharOrderPayment";
import { CustomTitle } from "../components/Custom/CustomTypography";
import { useAlert } from "../components/Alert";

export default function BiocharOrderPaymentPage() {
  const navigate = useNavigate();
  const { biocharOrderId } = useParams();
  const { t } = useTranslation();
  const [biocharOrderData, setBiocharOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { showAlert } = useAlert();

  useEffect(() => {
    fetchBiocharOrderDetails();

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const fetchBiocharOrderDetails = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getBiocharOrderDetails(biocharOrderId);
      setBiocharOrderData(data.data);
    } catch (err) {
      console.error("Error fetching order details:", err);
      setError("Failed to fetch order details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (payment_data, biocharOrderId) => {
    try {
      setLoading(true);
      const successful = await capturePayment(
        biocharOrderId,
        payment_data.razorpay_order_id,
        payment_data.razorpay_payment_id,
        payment_data.razorpay_signature
      );
      if (successful) {
        showAlert("success", "Payment successful");
        navigate(-1);
      }
      setLoading(false);
    } catch (err) {
      console.error("Payment verification failed:", err);
      setError("Payment verification failed. Please try again.");
      setLoading(false);
    }
  };

  const handleCashPayment = async (orderId) => {
    try {
      setLoading(true);
      const result = await processCashPayment(orderId);
      if (result.status === "SUCCESS") {
        showAlert("success", "Cash payment recorded successfully!");
        // Refresh the order data to show updated status
        await fetchBiocharOrderDetails();
      } else {
        showAlert("error", result.message || "Failed to process cash payment");
      }
    } catch (err) {
      console.error("Cash payment processing failed:", err);
      showAlert("error", "Failed to process cash payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (orderId) => {
    try {
      setLoading(true);
      const result = await createOrder(orderId);
      const rzp_orderId = result.razorpay_order_id;

      console.log("Rzp id :", rzp_orderId);

      const options = {
        key: RZP_KEY,
        currency: "INR",
        name: "Farmapp subscription",
        description: "Subscribe to farmapp",
        image:
          "https://farmapp-stage.asqi.in/static/media/FarmAppLogo.73816e8df129dc26c289.png",
        order_id: rzp_orderId,
        handler: function (response) {
          verifyPayment(response, orderId);
        },
      };
      let rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error("Payment initiation failed:", err);
      setError("Payment initiation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Biochar Order Payment - Farmapp</title>
      </Helmet>
      <Container maxWidth="xs">
        <div>
          <Grid
            container
            justify="center"
            direction="column"
            style={{ marginTop: "10px" }}
            spacing={2}
          >
            <Grid item>
              <CustomTitle>{t("Biochar Order Details")}</CustomTitle>
            </Grid>

            {error && (
              <Grid item>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              </Grid>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
              </Box>
            ) : biocharOrderData ? (
              <Grid item>
                <Paper
                  elevation={3}
                  style={{
                    padding: "20px",
                    backgroundColor: "#f9f9f9",
                    borderRadius: "12px",
                  }}
                >
                  <Grid container spacing={2}>
                    {/* Order ID and Status */}
                    <Grid item xs={6}>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        fontWeight="bold"
                      >
                        {t("Order ID")}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        #{biocharOrderData.id}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        fontWeight="bold"
                      >
                        {t("Status")}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color:
                            biocharOrderData.order_status === "UNPAID"
                              ? "#f44336"
                              : "#4caf50",
                          fontWeight: "medium",
                        }}
                        gutterBottom
                      >
                        {biocharOrderData.order_status}
                      </Typography>
                    </Grid>

                    {/* Quantity and Cost Details */}
                    <Grid item xs={6}>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        fontWeight="bold"
                      >
                        {t("Quantity")}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {biocharOrderData.requested_quantity} kg
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        fontWeight="bold"
                      >
                        {t("Cost per kg")}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        ₹{biocharOrderData.cost_per_kg}
                      </Typography>
                    </Grid>

                    {/* Charges */}
                    <Grid item xs={6}>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        fontWeight="bold"
                      >
                        {t("Delivery Charges")}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        ₹{biocharOrderData.delivery_charges}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        fontWeight="bold"
                      >
                        {t("Packaging Charges")}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        ₹{biocharOrderData.packaging_charges}
                      </Typography>
                    </Grid>

                    {/* Commission and Total */}
                    <Grid item xs={6}>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        fontWeight="bold"
                      >
                        {t("Commission")}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        ₹{biocharOrderData.commission}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        fontWeight="bold"
                      >
                        {t("Total Amount")}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: "#1976d2", fontWeight: "bold" }}
                        gutterBottom
                      >
                        ₹{biocharOrderData.total_amount_payable}
                      </Typography>
                    </Grid>

                    {/* Delivery Details */}
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        fontWeight="bold"
                      >
                        {t("Payment Mode")}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {biocharOrderData.payment_mode === "CASH"
                          ? t("Cash Payment")
                          : t("Digital Payment")}
                      </Typography>
                    </Grid>

                    {/* Delivery Details */}
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        fontWeight="bold"
                      >
                        {t("Delivery Type")}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {biocharOrderData.is_self_pickup
                          ? t("Self Pickup")
                          : t("Home Delivery")}
                      </Typography>
                    </Grid>

                    {/* Address */}
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        fontWeight="bold"
                      >
                        {t("Delivery Address")}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {biocharOrderData.address_line1},{" "}
                        {biocharOrderData.village}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ) : (
              <Box sx={{ textAlign: "center", marginTop: 3 }}>
                <Typography variant="body1">
                  {t("No order details found")}
                </Typography>
              </Box>
            )}

            {biocharOrderData && biocharOrderData.order_status === "UNPAID" && (
              <>
                {biocharOrderData.payment_mode === "CASH" && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        {t(
                          "This order is set for cash payment. Click the button below to mark the payment as completed and generate an invoice."
                        )}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
                <Grid item sx={{ mt: 1 }}>
                  {biocharOrderData.payment_mode === "CASH" ? (
                    <CustomButton
                      onClick={() => handleCashPayment(biocharOrderData.id)}
                      fullWidth
                      style={{ backgroundColor: "#4CAF50" }}
                    >
                      {t("Mark Cash Payment as Done")}
                    </CustomButton>
                  ) : (
                    <CustomButton
                      onClick={() => handleSubscribe(biocharOrderData.id)}
                      fullWidth
                    >
                      {t("Pay")} ₹{biocharOrderData.total_amount_payable}
                    </CustomButton>
                  )}
                </Grid>
              </>
            )}

            <Grid item sx={{ mt: 1 }}>
              <Button
                onClick={() => navigate(-1)}
                variant="outlined"
                fullWidth
                sx={{ borderRadius: "4px", py: 1 }}
              >
                {t("Back")}
              </Button>
            </Grid>
          </Grid>
        </div>
      </Container>
    </>
  );
}
